export interface Slot {
  date_time: string;
  service_id: string;
  employee_id: string;
  room_id: string;
  appointment_id: string | null;
  club_title?: string;
}

export interface Trainer {
  id: string;
  name: string;
  club_id?: string;
}

export interface Appointment {
  club_id: string;
  appointment_id: string;
  start_date: string;
  end_date: string;
  employee: {
    id: string;
    name: string;
  };
  service: {
    title: string;
    id: string;
  };
  room: {
    id: string;
    title: string;
  };
  status: string;
}

/**
 * Check if booking is available for a slot based on time constraints
 */
export const bookingIsAvailable = (slot: Slot): boolean => {
  const currentTime = new Date();
  const exerciseTime = new Date(slot.date_time);
  const tomorrow = new Date();
  tomorrow.setDate(currentTime.getDate() + 1);

  // allow booking after tomorrow
  if (exerciseTime > tomorrow) return true;

  // disable booking after 21 for next day morning
  if (currentTime.getHours() > 21 && exerciseTime.getHours() < 10) return false;

  // disable booking one hour before exercise
  if (exerciseTime < new Date(currentTime.getTime() + 60 * 60 * 1000)) return false;

  // disable booking in the morning of today
  if (exerciseTime.toDateString() === currentTime.toDateString() && exerciseTime.getHours() < 10)
    return false;

  return true;
};

/**
 * Check if a booking can be canceled based on time constraints
 */
export const cancelBookingIsAvailable = (targetTime: string): boolean => {
  const currentTime = new Date();
  const exerciseTime = new Date(targetTime);

  // If target time is after tomorrow's end of day
  const tomorrow = new Date();
  tomorrow.setDate(currentTime.getDate() + 1);
  tomorrow.setHours(23, 59, 59, 999);

  if (exerciseTime > tomorrow) {
    return true;
  }

  // If target time is after tomorrow and current time is before 18:00
  const nextDay = new Date();
  nextDay.setDate(currentTime.getDate() + 1);
  nextDay.setHours(0, 0, 0, 0);

  if (exerciseTime > nextDay && currentTime.getHours() < 18) {
    return true;
  }

  return false;
};

const SAME_DAY_CANCEL_ALLOWED_PHONES = ["9645338082"];

export const isSameDayCancellationAllowedForPhone = (phone: string): boolean => {
  const digits = phone.replace(/\D/g, "");
  return SAME_DAY_CANCEL_ALLOWED_PHONES.some((p) => digits.endsWith(p));
};

/**
 * Check if same-day cancellation is available.
 * Available when: regular cancel is unavailable, class is today and hasn't started, and current time is before 10:00.
 */
export const isSameDayCancellationAvailable = (targetTime: string): boolean => {
  if (cancelBookingIsAvailable(targetTime)) {
    return false;
  }

  const now = new Date();
  const exerciseTime = new Date(targetTime);

  // Class must not have started yet
  if (exerciseTime <= now) {
    return false;
  }

  // Class must be today
  if (
    exerciseTime.getFullYear() !== now.getFullYear() ||
    exerciseTime.getMonth() !== now.getMonth() ||
    exerciseTime.getDate() !== now.getDate()
  ) {
    return false;
  }

  // Current time must be before 10:00
  return now.getHours() < 10;
};
